import React, { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Sparkles, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, getMl } from "@/lib/api";
import MultilingualField from "@/components/MultilingualField";
import FileUploader from "@/components/FileUploader";

const empty = () => ({
  type: "export", category_id: "",
  name: { en: "", tr: "", id: "" },
  short_description: { en: "", tr: "", id: "" },
  description: { en: "", tr: "", id: "" },
  images: [], origin_country: "", grade: "", moq: "",
  packaging: { en: "", tr: "", id: "" },
  documents_available: { en: "", tr: "", id: "" },
  loading_port: "", monthly_capacity: "",
  specifications: [], catalogue_pdf: "",
  price_visible: false, price_text: { en: "", tr: "", id: "" },
  social_links: { instagram: "", facebook: "", tiktok: "", linkedin: "", youtube: "", whatsapp: "" },
  seo_title: { en: "", tr: "", id: "" },
  seo_description: { en: "", tr: "", id: "" },
  is_featured: false, is_published: true,
});

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState("all");

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/products?published_only=false"),
      api.get("/categories"),
    ]);
    setItems(p.data); setCategories(c.data);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (data) => {
    try {
      if (editing?.id) await api.put(`/products/${editing.id}`, data);
      else await api.post("/products", data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error("Failed: " + (e?.response?.data?.detail || "")); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = filterType === "all" ? items : items.filter((p) => p.type === filterType);

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h1 className="font-display text-3xl text-[#0A1128]">Products</h1>
        <div className="flex items-center gap-3">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="product-filter-type" className="px-4 py-2.5 border border-slate-300 rounded-sm text-xs uppercase tracking-[0.15em]">
            <option value="all">All Types</option>
            <option value="export">Export</option>
            <option value="import">Import</option>
          </select>
          <button onClick={() => setEditing(empty())} data-testid="add-product-btn" className="btn-gold"><Plus size={14} /> Add Product</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Image</th>
              <th className="px-4 py-3 text-left">Name (EN)</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Featured</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const cat = categories.find((c) => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-3"><div className="w-12 h-12 bg-slate-100 rounded-sm overflow-hidden">{p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover" alt="" />}</div></td>
                  <td className="px-4 py-3 font-medium">{getMl(p.name, "en")}</td>
                  <td className="px-4 py-3 capitalize">{p.type}</td>
                  <td className="px-4 py-3 text-slate-600">{cat ? getMl(cat.name, "en") : "—"}</td>
                  <td className="px-4 py-3">{p.is_featured ? "★" : ""}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-sm uppercase tracking-[0.15em] ${p.is_published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{p.is_published ? "Live" : "Draft"}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(p)} data-testid={`edit-prod-${p.id}`} className="text-slate-400 hover:text-[#0A1128] p-1.5"><Pencil size={14} /></button>
                    <button onClick={() => onDelete(p.id)} data-testid={`delete-prod-${p.id}`} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <ProductForm initial={editing} categories={categories} onSave={onSave} onClose={() => setEditing(null)} />}
    </div>
  );
};

const ProductForm = ({ initial, categories, onSave, onClose }) => {
  const [data, setData] = useState({ ...empty(), ...initial });
  const [aiOpen, setAiOpen] = useState(false);
  const cats = categories.filter((c) => c.type === data.type);

  const addSpec = () => setData({ ...data, specifications: [...(data.specifications || []), { key: "", value: "" }] });
  const setSpec = (i, k, v) => {
    const arr = [...(data.specifications || [])];
    arr[i] = { ...arr[i], [k]: v };
    setData({ ...data, specifications: arr });
  };
  const delSpec = (i) => setData({ ...data, specifications: data.specifications.filter((_, idx) => idx !== i) });

  const setSocial = (k, v) => setData({ ...data, social_links: { ...data.social_links, [k]: v } });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="product-form-modal">
      <div className="bg-white max-w-4xl w-full rounded-sm my-10">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="font-display text-2xl">{initial.id ? "Edit" : "Add"} Product</h2>
          <div className="flex items-center gap-2">
            {!initial.id && (
              <button
                type="button"
                onClick={() => setAiOpen(true)}
                data-testid="ai-autofill-btn"
                className="inline-flex items-center gap-2 bg-[#0A1128] text-[#D4AF37] hover:bg-[#161E32] transition-colors px-4 py-2 rounded-sm text-xs uppercase tracking-[0.18em] font-semibold"
              >
                <Sparkles size={14} /> AI Auto-fill
              </button>
            )}
            <button onClick={onClose} data-testid="close-prod-form" className="p-2 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>
        {aiOpen && (
          <AiExtractModal
            productType={data.type}
            onApply={(extracted) => {
              setData((d) => ({
                ...d,
                name: extracted.name || d.name,
                short_description: extracted.short_description || d.short_description,
                description: extracted.description || d.description,
                origin_country: extracted.origin_country || d.origin_country,
                grade: extracted.grade || d.grade,
                moq: extracted.moq || d.moq,
                packaging: extracted.packaging || d.packaging,
                documents_available: extracted.documents_available || d.documents_available,
                loading_port: extracted.loading_port || d.loading_port,
                monthly_capacity: extracted.monthly_capacity || d.monthly_capacity,
                specifications: extracted.specifications?.length ? extracted.specifications : d.specifications,
                price_text: extracted.price_text || d.price_text,
                seo_title: extracted.seo_title || d.seo_title,
                seo_description: extracted.seo_description || d.seo_description,
                category_id: extracted.category_id || d.category_id,
              }));
              setAiOpen(false);
              toast.success("AI filled the form. Review, upload images, and save.");
            }}
            onClose={() => setAiOpen(false)}
          />
        )}
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Type</label>
              <select value={data.type} onChange={(e) => setData({ ...data, type: e.target.value, category_id: "" })} data-testid="prod-type" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm">
                <option value="export">Export</option>
                <option value="import">Import</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Category *</label>
              <select value={data.category_id} onChange={(e) => setData({ ...data, category_id: e.target.value })} required data-testid="prod-category" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm">
                <option value="">Select...</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{getMl(c.name, "en")}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Origin Country</label>
              <input value={data.origin_country} onChange={(e) => setData({ ...data, origin_country: e.target.value })} data-testid="prod-origin" className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
            </div>
          </div>

          <MultilingualField label="Name" value={data.name} onChange={(v) => setData({ ...data, name: v })} testId="prod-name" required />
          <MultilingualField label="Short Description" value={data.short_description} onChange={(v) => setData({ ...data, short_description: v })} testId="prod-short" multiline />
          <MultilingualField label="Full Description" value={data.description} onChange={(v) => setData({ ...data, description: v })} testId="prod-desc" multiline />

          <FileUploader label="Product Images" multi accept="image/*" value={data.images} onChange={(v) => setData({ ...data, images: v })} testId="prod-images" />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Input label="Grade" v={data.grade} on={(v) => setData({ ...data, grade: v })} testId="prod-grade" />
            <Input label="MOQ" v={data.moq} on={(v) => setData({ ...data, moq: v })} testId="prod-moq" />
            <Input label="Loading Port" v={data.loading_port} on={(v) => setData({ ...data, loading_port: v })} testId="prod-port" />
            <Input label="Monthly Capacity" v={data.monthly_capacity} on={(v) => setData({ ...data, monthly_capacity: v })} testId="prod-capacity" />
          </div>

          <MultilingualField label="Packaging" value={data.packaging} onChange={(v) => setData({ ...data, packaging: v })} testId="prod-packaging" />
          <MultilingualField label="Documents Available" value={data.documents_available} onChange={(v) => setData({ ...data, documents_available: v })} testId="prod-docs" />

          {/* Specifications */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Specifications</label>
              <button type="button" onClick={addSpec} data-testid="add-spec" className="text-xs text-[#D4AF37] uppercase tracking-[0.2em] font-semibold">+ Add</button>
            </div>
            <div className="space-y-2">
              {(data.specifications || []).map((sp, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input value={sp.key} onChange={(e) => setSpec(i, "key", e.target.value)} placeholder="Key" data-testid={`spec-key-${i}`} className="col-span-5 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  <input value={sp.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="Value" data-testid={`spec-value-${i}`} className="col-span-6 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  <button type="button" onClick={() => delSpec(i)} data-testid={`del-spec-${i}`} className="col-span-1 text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Catalogue PDF */}
          <FileUploader label="Catalogue PDF" accept="application/pdf" value={data.catalogue_pdf} onChange={(v) => setData({ ...data, catalogue_pdf: v })} testId="prod-pdf" />

          {/* Pricing */}
          <div className="bg-slate-50 p-5 rounded-sm space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={data.price_visible} onChange={(e) => setData({ ...data, price_visible: e.target.checked })} data-testid="prod-price-visible" />
              <span>Show price publicly</span>
            </label>
            <MultilingualField label='Price text (e.g. "Request Latest Quote")' value={data.price_text} onChange={(v) => setData({ ...data, price_text: v })} testId="prod-price-text" />
          </div>

          {/* Social */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-3">Product Social Links (optional)</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {["instagram", "facebook", "tiktok", "linkedin", "youtube", "whatsapp"].map((k) => (
                <input key={k} placeholder={k} value={data.social_links?.[k] || ""} onChange={(e) => setSocial(k, e.target.value)} data-testid={`prod-social-${k}`} className="px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              ))}
            </div>
          </div>

          {/* Flags */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.is_featured} onChange={(e) => setData({ ...data, is_featured: e.target.checked })} data-testid="prod-featured" /> Featured on homepage</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.is_published} onChange={(e) => setData({ ...data, is_published: e.target.checked })} data-testid="prod-published" /> Published</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={onClose} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5">Cancel</button>
          <button onClick={() => onSave(data)} data-testid="prod-save" className="btn-gold">Save</button>
        </div>
      </div>
    </div>
  );
};

const Input = ({ label, v, on, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">{label}</label>
    <input value={v} onChange={(e) => on(e.target.value)} data-testid={testId} className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm" />
  </div>
);

const AiExtractModal = ({ productType, onApply, onClose }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!text.trim() && !file) {
      toast.error("Paste product text or attach a PDF");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("text", text || "");
      fd.append("product_type", productType);
      if (file) fd.append("file", file);
      const res = await api.post("/ai/extract-product", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      onApply(res.data);
    } catch (e) {
      toast.error("AI extract failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-6" data-testid="ai-extract-modal">
      <div className="bg-white max-w-2xl w-full rounded-sm">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0A1128] flex items-center justify-center rounded-sm">
              <Sparkles size={16} className="text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-display text-xl text-[#0A1128]">AI Product Auto-fill</h3>
              <p className="text-[11px] text-slate-500">Powered by Claude Sonnet 4.5 — fills every field in EN / TR / ID</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-ai-modal" className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 block mb-2">
              Paste product info (spec sheet, brochure text, supplier notes…)
            </label>
            <textarea
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Premium Robusta Coffee Grade A. Origin: Lampung, Indonesia. Moisture: max 12.5%. MOQ: 1x20FCL. Packaging: 60kg jute bags. Documents: COA, COO, Phytosanitary. Loading port: Tanjung Priok. Monthly capacity: 500-1000 MT."
              data-testid="ai-text-input"
              className="w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">or attach a PDF</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <div>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,.pdf,.txt"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="ai-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="ai-file-btn"
              className="w-full border border-dashed border-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-500 text-xs uppercase tracking-[0.2em] px-5 py-4 rounded-sm flex items-center justify-center gap-2"
            >
              <Upload size={14} />
              {file ? file.name : "Upload spec-sheet PDF"}
            </button>
            {file && (
              <button
                type="button"
                onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                data-testid="ai-file-clear"
                className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-2 hover:text-red-500"
              >
                Remove file
              </button>
            )}
          </div>
          <div className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-4 text-xs text-slate-600">
            <p className="font-semibold text-[#0A1128] mb-1">What AI fills for you:</p>
            <p>Name, short & full description (EN/TR/ID), origin, grade, MOQ, packaging, documents, loading port, monthly capacity, specifications, price text, SEO fields, and best-matching category.</p>
            <p className="mt-2 text-slate-500">You still upload product images and catalogue PDF manually.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <button onClick={onClose} disabled={loading} className="text-xs uppercase tracking-[0.2em] text-slate-500 px-5 disabled:opacity-50">Cancel</button>
          <button onClick={submit} disabled={loading} data-testid="ai-extract-run" className="btn-gold disabled:opacity-50">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Extracting…</> : <><Sparkles size={14} /> Extract & Fill</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
